import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import Button from "@insite/mobius/Button";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import Select, { SelectProps } from "@insite/mobius/Select";
import TextArea from "@insite/mobius/TextArea";
import Typography from "@insite/mobius/Typography";
import RichTextEditor from "@insite/shell/Components/Elements/RichTextEditor";
import AxiomIcon from "@insite/shell/Components/Icons/AxiomIcon";
import { ChatMessage } from "@insite/shell/Services/ContentAdminService";
import shellTheme from "@insite/shell/ShellTheme";
import {
    closeSocialPostGenerationModal,
    generateSocialMediaPost,
} from "@insite/shell/Store/PageEditor/PageEditorActionCreators";
import ShellState from "@insite/shell/Store/ShellState";
import { TS } from "@insite/shell/TS";
import * as React from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ShellState) => {
    const currentPage = getCurrentPage(state);
    let pageId = currentPage.id;
    if (currentPage.type === "VariantRootPage") {
        const defaultVariant = state.pageTree.treeNodesByParentId[currentPage.nodeId]?.find(o => o.isDefaultVariant);
        pageId = defaultVariant ? defaultVariant.pageId : "";
    }

    return {
        pageUrl: `/Content/Page/${pageId}`,
        isOpen: state.pageEditor.displaySocialPostGenerationModal,
    };
};

const mapDispatchToProps = {
    closeSocialPostGenerationModal,
    generateSocialMediaPost,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const SocialPostGenerationModal = ({
    pageUrl,
    isOpen,
    closeSocialPostGenerationModal,
    generateSocialMediaPost,
}: Props) => {
    const [socialPlatform, setSocialPlatform] = useState<string>("Facebook");
    const [prompt, setPrompt] = useState<string>(
        TS(
            "Generate engaging social media post based on the content of the website page. Be creative and make sure to capture the essence of the content in an engaging and concise manner.",
        ),
    );
    const [result, setResult] = useState<string>();
    const [isGenerating, setIsGenerating] = useState<boolean>(false);

    const copyGeneratedPost = (event: React.MouseEvent<HTMLButtonElement>) => {
        const text = result && stripHtml(result);
        text && navigator.clipboard.writeText(text);
    };

    const stripHtml = (html: string) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        return doc.body.outerText || "";
    };

    const handlePlatformChange: SelectProps["onChange"] = event => {
        setSocialPlatform(event.target.value);
    };

    const promptChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setPrompt(event.target.value);
    };

    const generateClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
        setIsGenerating(true);
        generateSocialMediaPost(
            pageUrl,
            socialPlatform,
            prompt,
            undefined,
            (response: ChatMessage[], errorMessage?: string) => {
                if (response?.length > 0) {
                    const message = response.find(x => x.role === "assistant");
                    setResult(message?.content ?? "");
                } else if (errorMessage) {
                    setResult(`${TS("Error")}: ${errorMessage}`);
                }

                setIsGenerating(false);
            },
        );
    };

    const resultChangeHandler = (newValue: string) => {
        setResult(newValue);
    };

    const closeHandler = () => {
        closeSocialPostGenerationModal();
        setResult(undefined);
    };

    return (
        <Modal
            isOpen={isOpen}
            handleClose={closeHandler}
            size={600}
            closeOnEsc
            closeOnScrimClick
            cssOverrides={{
                modalTitle: css`
                    display: none;
                `,
                modalContent: css`
                    padding: 20px;
                    background: white;
                `,
            }}
        >
            <Typography as="h2">{TS("Generate Social Post")}</Typography>
            <Typography>
                {TS("Effortlessly create tailored social media posts from the current webpage in CMS.")}
            </Typography>
            <Select
                label={TS("Choose Platform")}
                value={socialPlatform}
                onChange={handlePlatformChange}
                labelProps={{ weight: "normal", transform: "inherit" }}
                cssOverrides={{
                    formInputWrapper: css`
                        margin-top: 5px !important;
                        width: 200px;
                    `,
                }}
            >
                <option value="Facebook">{TS("Facebook")}</option>
                <option value="Instagram">{TS("Instagram")}</option>
                <option value="Twitter">{TS("Twitter")}</option>
            </Select>
            <TextArea
                label={TS("Prompt")}
                value={prompt}
                onChange={promptChangeHandler}
                labelProps={{ weight: "normal", transform: "inherit" }}
                cssOverrides={{
                    formInputWrapper: css`
                        margin-top: 5px !important;
                    `,
                }}
            />
            <ButtonWrapper>
                <Button
                    variant="secondary"
                    onClick={generateClickHandler}
                    disabled={isGenerating}
                    size={36}
                    mergeCss={true}
                    css={css`
                        margin-top: 3px;
                        padding: 0 12px;
                        font-size: 15px;
                        &:disabled {
                            background: ${({ theme }) => theme.colors.custom.buttonOutlinedHoverBackgroundColor};
                            border: 2px solid ${({ theme }) => theme.colors.custom.buttonHoverActiveBorderColor};
                            color: #bbb;
                        }
                        &:disabled :hover i {
                            color: #bbb;
                        }
                        &:hover i {
                            color: ${shellTheme.colors.custom.buttonOutlinedHoverLabelColor};
                        }
                    `}
                >
                    <AxiomIcon
                        src="refresh"
                        size={15}
                        color={isGenerating ? "#bbb;" : shellTheme.colors.text.link}
                        css={css`
                            padding-right: 5px;
                        `}
                    />
                    {TS(result ? "Regenerate" : "Generate")}
                </Button>
            </ButtonWrapper>
            <Label>{TS("Result")}</Label>
            {isGenerating && (
                <LoadingSpinnerWrapper>
                    <LoadingSpinner />
                </LoadingSpinnerWrapper>
            )}
            {!isGenerating && (
                <ResultWrapper>
                    <RichTextEditor
                        name="Result"
                        value={result}
                        extendedConfig={{
                            paragraphMultipleStyles: false,
                            pastePlain: true,
                            codeViewKeepActiveButtons: [`popinResult`, `popoutResult`, "html"],
                            codeMirrorOptions: {
                                lineNumbers: false,
                            },
                            htmlRemoveTags: [],
                            height: 150,
                        }}
                        expandedToolbarButtons={{
                            moreMisc: {},
                            code: {},
                        }}
                        onChange={resultChangeHandler}
                    />
                </ResultWrapper>
            )}
            <ButtonWrapper>
                <Button
                    variant="secondary"
                    onClick={copyGeneratedPost}
                    typographyProps={{ transform: "inherit", weight: "normal" }}
                    size={36}
                    disabled={!result}
                    mergeCss={true}
                    css={css`
                        margin-top: 8px;
                        padding: 0 12px;
                        font-size: 15px;
                        &:disabled {
                            background: ${({ theme }) => theme.colors.custom.buttonOutlinedHoverBackgroundColor};
                            border: 2px solid ${({ theme }) => theme.colors.custom.buttonHoverActiveBorderColor};
                            color: #bbb;
                        }
                    `}
                >
                    {TS("Copy")}
                </Button>
            </ButtonWrapper>
            <BottomButtonWrapper>
                <Button variant="primary" onClick={closeHandler}>
                    {TS("Close")}
                </Button>
            </BottomButtonWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SocialPostGenerationModal);

const ButtonWrapper = styled.div`
    text-align: right;
`;

const BottomButtonWrapper = styled.div`
    text-align: right;
    margin: 18px -20px -20px -20px;
    padding: 13px 20px;
    background-color: ${({ theme }) => theme.colors.common.accent};
`;

const Label = styled.label`
    display: block;
    font-size: 1rem;
    line-height: 21px;
    margin: 25px 0 -4px;
    position: relative;
`;

const LoadingSpinnerWrapper = styled.div`
    text-align: center;
    padding-top: 60px;
    height: 150px;
`;

const ResultWrapper = styled.div`
    height: 150px;
    display: inline;
`;
